import { BittsIconPrefix } from '@crossbeam/bitts/src/types';
import { GetApiPayloadByUrl } from '@crossbeam/openapi';

import { isEqual } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { crossbeamApi } from '@/api';
import usePopulations from '@/composables/usePopulations';
import {
  PARTNER_POPULATION_RULE,
  RuleType,
  SHARING,
  SHARING_TYPES,
  SharingType,
} from '@/constants/data_shares';
import { isGoogleOrCsvSource } from '@/constants/data_sources';
import { STANDARD_POPULATION_ORDER } from '@/constants/standard_populations';
import {
  useDataRequestsStore,
  useDataSharePresetsStore,
  useDataSharesStore,
  useFlashesStore,
} from '@/stores';
import { DataSharePreset } from '@/types/data_share_presets';
import { OutgoingSharingRule } from '@/types/data_shares';

export type DataShareItem = {
  id: number | null;
  partnerId: number | null;
  feedId: number;
  sourceId?: number;
  presetParentId: number; // feedId or sourceId
  populationId: number;
  populationName: string;
  visibility: SharingType;
  presetId?: number | null;
  integrationType?: string;
};

type PresetOptionItem = {
  value: number | string;
  label: string;
  icon?: [BittsIconPrefix, string];
};

export type PresetOptionsGroup = {
  heading: string | null;
  items: PresetOptionItem[];
};

export type PopulationOptionItem = {
  label: string;
  value: number;
  defaultPresetId?: number;
};

type OutgoingRule = {
  id: number | null;
  partner_org_id: number | null;
  population_id: number;
  visibility: SharingType;
  data_share_presets_id: number | null;
};

export type PopulationToPopulationMap = Record<
  number,
  Record<number, number[]>
>;

interface PopulationToPopulationSetting {
  partnerPopulation?: {
    id: number;
    name: string;
  };
  dataShare?: {
    id: number;
  };
  selectedSourceFieldIds?: number[];
}

type Assignments = GetApiPayloadByUrl<'/v0.1/data-share-assignments', 'post'>;
type DataShare = Assignments['data_shares'][0];

const currentPopulationToPopulation = ref<PopulationToPopulationMap>({});
const initialPopulationToPopulation = ref<PopulationToPopulationMap>({});
const dataShares = ref<DataShareItem[]>([]);
const initialDataShares = ref<DataShareItem[]>([]);

export default function (partnerId: number | null = null) {
  const isLoading = ref(false);
  const allSharingRules = ref<OutgoingSharingRule[]>([]);
  const defaultDataSharesMap = ref<Record<number, DataShareItem>>({});
  const hasChanges = ref(false);
  const bulkPayload = ref<Assignments>({
    partner_org_id: partnerId,
    data_shares: [],
  });
  const shareIdsToDelete = ref<Record<number, number[]>>({});

  const route = useRoute();
  const dataSharesStore = useDataSharesStore();
  const { refreshDataRequestsStore } = useDataRequestsStore();
  const { ready, allPresets } = storeToRefs(useDataSharePresetsStore());
  const { standardPopulations, customPopsWithStatsAndSharing } = usePopulations(
    partnerId as never,
  );
  const allPopulations = computed(() => [
    ...standardPopulations.value,
    ...customPopsWithStatsAndSharing.value,
  ]);

  const populationOptions = computed(() =>
    allPopulations.value
      .filter((population) => {
        return !dataShares.value.some(
          (dataShare: DataShareItem) =>
            dataShare.populationId === population.id,
        );
      })
      .map((population) => {
        const isGoogleOrCsv = isGoogleOrCsvSource(population.integration_type);
        const parentId = isGoogleOrCsv
          ? population.source_id
          : population.feed_id;
        return {
          label: population.name,
          value: population.id,
          defaultPresetId:
            presetOptions.value[parentId]?.[0]?.items[0]?.value ?? null,
        } as PopulationOptionItem;
      }),
  );

  const isPartnerSettings = computed(() => partnerId !== null);

  const prefillPopShares = computed(
    () => route.name === 'onboarding_data_sharing',
  );

  const presetOptions = computed(() =>
    getPresetOptionsPerFeed(allPresets.value),
  );

  const singleSharePresets = computed(() => {
    return allPresets.value.filter(
      (preset) => preset.preset_type === 'single_share_preset',
    );
  });

  onMounted(() => {
    if (ready.value) {
      getDataShares();
    } else {
      const stop = watch(ready, (newVal) => {
        if (newVal === true) {
          getDataShares();
          stop();
        }
      });
    }
  });

  function toDataShareItem(rule: OutgoingRule): DataShareItem {
    const pop = allPopulations.value.find(
      (population) => population.id === rule.population_id,
    );
    const isGoogleOrCsv = isGoogleOrCsvSource(pop?.integration_type);
    return {
      id: rule.id,
      partnerId: rule.partner_org_id,
      populationId: pop?.id,
      populationName: pop?.name,
      integrationType: pop?.integration_type,
      visibility: rule.visibility as SharingType,
      presetId: rule.data_share_presets_id,
      feedId: pop?.feed_id,
      sourceId: isGoogleOrCsv ? pop?.source_id : undefined,
      presetParentId: isGoogleOrCsv ? pop?.source_id : pop?.feed_id,
    };
  }

  async function getDataShares() {
    isLoading.value = true;
    const { data, error } = await crossbeamApi.GET(
      '/v0.1/outgoing-sharing-rules',
    );
    if (error) throw error;

    allSharingRules.value = data.items;

    // Compute default rules
    const defaultRulesMap: Record<number, DataShareItem> = {};
    data.items
      .filter((rule: OutgoingSharingRule) => rule.rule_type === 'population')
      .forEach((rule: OutgoingSharingRule) => {
        defaultRulesMap[rule.population_id] = toDataShareItem(rule);
      });
    defaultDataSharesMap.value = defaultRulesMap;

    // Compute active rules (default if no partnerId, otherwise default + partner)
    const activeRulesMap: Record<number, DataShareItem> = {
      ...defaultRulesMap,
    };
    if (isPartnerSettings.value) {
      data.items
        .filter(
          (rule: OutgoingSharingRule) =>
            rule.rule_type === 'partner' && rule.partner_org_id === partnerId,
        )
        .forEach((rule: OutgoingSharingRule) => {
          activeRulesMap[rule.population_id] = toDataShareItem(rule);
        });
    }

    const activeShares: DataShareItem[] = Object.values(activeRulesMap).filter(
      (share) => share.visibility !== SHARING_TYPES.HIDDEN,
    );

    if (activeShares.length) {
      dataShares.value = activeShares;
      initialDataShares.value = [...activeShares];

      // Initialize population to population settings
      const populationToPopulationMap = computed<PopulationToPopulationMap>(
        () => {
          const resultMap: PopulationToPopulationMap = {};
          if (!allPopulations.value?.length) {
            return resultMap;
          }
          allPopulations.value.forEach((population) => {
            if (!population.populationToPopulationSettings?.length) {
              return;
            }
            if (!resultMap[population.id]) {
              resultMap[population.id] = {};
            }
            population.populationToPopulationSettings.forEach(
              (setting: PopulationToPopulationSetting) => {
                if (!setting.partnerPopulation?.id) {
                  return;
                }
                const partnerPopulationId = setting.partnerPopulation.id;
                const populationMap = resultMap[population.id];
                if (populationMap) {
                  populationMap[partnerPopulationId] =
                    setting.selectedSourceFieldIds || [];
                }
              },
            );
          });
          return resultMap;
        },
      );
      initialPopulationToPopulation.value = populationToPopulationMap.value;
      currentPopulationToPopulation.value = {
        ...populationToPopulationMap.value,
      };
    } else if (prefillPopShares.value) {
      initializeDefaultShares();
    }

    isLoading.value = false;
  }

  function initializeDefaultShares() {
    populationOptions.value.forEach((option) =>
      createDefaultDataShare({
        label: option.label,
        value: option.value,
        defaultPresetId: option.defaultPresetId,
      }),
    );
  }

  function createDataShare({
    populationId,
    visibility,
    presetId,
  }: {
    populationId: number;
    visibility: SharingType;
    presetId: number | null;
  }) {
    const existingRule = allSharingRules.value.find(
      (rule) =>
        rule.population_id === populationId &&
        rule.partner_org_id === partnerId,
    );

    const { newPresetId } = processNewItem(populationId, visibility, presetId);

    // Remove the population from the local array if it already exists
    dataShares.value = dataShares.value.filter(
      (share) => share.populationId !== populationId,
    );

    // Add the new data share to the local array
    if (visibility !== SHARING_TYPES.HIDDEN) {
      dataShares.value.push(
        toDataShareItem({
          id: existingRule?.id ?? null,
          partner_org_id: partnerId,
          population_id: populationId,
          visibility,
          data_share_presets_id: newPresetId,
        }),
      );
    }
  }

  function createDefaultDataShare({
    value: populationId,
    defaultPresetId,
  }: PopulationOptionItem) {
    createDataShare({
      populationId,
      visibility: SHARING_TYPES.SHARING,
      presetId: defaultPresetId ?? null,
    });
  }

  function createCustomDataShare({
    populationId,
    visibility,
    presetId,
  }: {
    populationId: number;
    visibility: SharingType;
    presetId?: number | null;
  }) {
    createDataShare({
      populationId,
      visibility,
      presetId: presetId ?? null,
    });
  }

  function updateDataShare({
    populationId,
    visibility,
    presetId,
  }: {
    populationId: number;
    visibility: SharingType;
    presetId?: number | null;
  }) {
    const { newPresetId } = processNewItem(populationId, visibility, presetId);

    if (visibility === SHARING_TYPES.HIDDEN) {
      dataShares.value = dataShares.value.filter(
        (share) => share.populationId !== populationId,
      );
    } else {
      const index = dataShares.value.findIndex(
        (share) => share.populationId === populationId,
      );
      dataShares.value[index] = {
        ...dataShares.value[index],
        visibility,
        presetId: newPresetId,
      } as DataShareItem;
    }
  }

  function resetDataShare({ id: shareId, populationId }: DataShareItem) {
    // delete pop from update if present in payload
    bulkPayload.value.data_shares = bulkPayload.value.data_shares.filter(
      (share) => share.population_id !== populationId,
    );
    // add rule id and pop to deletion list
    if (shareId) {
      if (!shareIdsToDelete.value[populationId]) {
        shareIdsToDelete.value[populationId] = [];
      }
      shareIdsToDelete.value[populationId].push(Number(shareId));
    }
    if (currentPopulationToPopulation.value[populationId]) {
      const partnerPopRules =
        dataSharesStore.outgoingSharingRuleMap?.partner_population;

      if (partnerPopRules) {
        Object.keys(partnerPopRules).forEach((partnerPopId) => {
          const rules = partnerPopRules[Number(partnerPopId)];

          if (rules) {
            rules.forEach((rule) => {
              if (
                rule.population_id === populationId &&
                rule?.partner_population_id
              ) {
                deleteOverride(
                  rule.id,
                  rule.partner_population_id,
                  rule.population_id,
                );
              }
            });
          }
        });
      }
    }
    // in dataShares, replace partner rule by default rule for the population id
    const defaultRule = defaultDataSharesMap.value?.[populationId];
    if (!defaultRule) {
      dataShares.value = dataShares.value.filter(
        (share) => share.populationId !== populationId,
      );
    } else {
      dataShares.value = dataShares.value.map((share) =>
        share.populationId === populationId ? defaultRule : share,
      );
    }
  }

  function deleteOverride(
    shareId: number | null,
    partnerPopulationId: number,
    populationId: number,
  ) {
    if (shareId) {
      if (!shareIdsToDelete.value[populationId]) {
        shareIdsToDelete.value[populationId] = [];
      }
      shareIdsToDelete.value[populationId].push(shareId);
      const updatedMap = { ...currentPopulationToPopulation.value };

      if (updatedMap[populationId]) {
        const updatedInnerMap = { ...updatedMap[populationId] };
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete updatedInnerMap[partnerPopulationId];
        updatedMap[populationId] = updatedInnerMap;
        if (Object.keys(updatedInnerMap).length === 0) {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete updatedMap[populationId];
        }
        currentPopulationToPopulation.value = updatedMap;
      }
    }
  }

  function addAndUpdateOverrides() {
    const settingsToAddAndUpdate = Object.keys(
      currentPopulationToPopulation.value,
    );
    const promises: Promise<unknown>[] = [];

    settingsToAddAndUpdate.forEach((populationId) => {
      const partnerOverrides =
        currentPopulationToPopulation.value[Number(populationId)];

      if (!partnerOverrides) return;
      createCustomDataShare({
        populationId: Number(populationId),
        visibility: SHARING,
        presetId: null,
      });

      Object.entries(partnerOverrides).forEach(
        ([partnerPopulationId, selectedSourceFields]) => {
          const partnerPopId = parseInt(partnerPopulationId);
          const popIdInt = parseInt(populationId);

          if (
            !Array.isArray(selectedSourceFields) ||
            selectedSourceFields.length === 0
          )
            return;

          const population = allPopulations.value.find(
            (pop) => pop.id === popIdInt,
          );

          if (!population || !population.populationToPopulationSettings) return;

          // Check if this is an existing setting that needs to be updated
          const setting = population.populationToPopulationSettings.find(
            (s: {
              partnerPopulation: { id: number };
              dataShare: { id: number };
            }) => s.partnerPopulation.id === partnerPopId,
          );

          const body = {
            rule_type: PARTNER_POPULATION_RULE as RuleType,
            partner_population_id: partnerPopId,
            partner_org_id: partnerId ?? undefined,
            population_id: popIdInt,
            visibility: SHARING as SharingType,
            source_field_ids: selectedSourceFields,
          };

          if (setting && setting.dataShare) {
            promises.push(
              crossbeamApi.PATCH('/v0.1/outgoing-sharing-rules/{id}', {
                body,
                params: { path: { id: setting.dataShare.id } },
              }),
            );
          } else {
            promises.push(
              crossbeamApi.POST('/v0.1/outgoing-sharing-rules', {
                body,
              }),
            );
          }
        },
      );
    });
    return Promise.all(promises);
  }

  async function bulkUpdateDataShares() {
    if (Object.keys(shareIdsToDelete.value).length > 0) {
      const deletionPromises = Object.entries(shareIdsToDelete.value).flatMap(
        ([_, ids]) =>
          ids.map((id: number) =>
            crossbeamApi.DELETE('/v0.1/outgoing-sharing-rules/{id}', {
              params: {
                path: {
                  id,
                },
              },
            }),
          ),
      );
      await Promise.all(deletionPromises);
    }

    await addAndUpdateOverrides();

    if (bulkPayload.value.data_shares.length > 0) {
      const { error } = await crossbeamApi.POST(
        '/v0.1/data-share-assignments',
        {
          body: bulkPayload.value,
        },
      );
      if (error) {
        const flashesStore = useFlashesStore();
        const errors = (error as { errors?: string[] }).errors || [
          'An error occurred while saving settings',
        ];
        errors.forEach((err) => flashesStore.addErrorFlash({ message: err }));
        throw error;
      }
    }
    shareIdsToDelete.value = {};
    bulkPayload.value.data_shares = [];
    await dataSharesStore.refreshDataSharesStore();
    await refreshDataRequestsStore();
    showSuccessToast();
    await getDataShares();
  }

  function processNewItem(
    populationId: number,
    visibility: SharingType,
    presetId?: number | null,
  ) {
    const population = allPopulations.value.find((p) => p.id === populationId);
    const isGoogleOrCsv = isGoogleOrCsvSource(population.integration_type);
    const parentId = isGoogleOrCsv ? population.source_id : population.feed_id;

    const recommendedPresetId = presetOptions.value[parentId]?.[0]?.items[0]
      ?.value as number;

    const newPresetId =
      visibility === SHARING_TYPES.OLAPS_ONLY ||
      visibility === SHARING_TYPES.HIDDEN
        ? null
        : (presetId ?? recommendedPresetId ?? null);

    const newItem = {
      population_id: populationId,
      visibility,
      preset_id: newPresetId,
    };

    addOrReplaceDataShare(bulkPayload.value, populationId, newItem);

    // Remove population from the shares to delete
    shareIdsToDelete.value = Object.fromEntries(
      Object.entries(shareIdsToDelete.value).filter(
        ([id]) => Number(id) !== populationId,
      ),
    );

    return { newPresetId };
  }

  // Track changes between current and initial state
  watch(
    [dataShares, currentPopulationToPopulation],
    () => {
      const sortedDataShares = [...dataShares.value].sort(sortPopulations);
      const sortedInitialDataShares = [...initialDataShares.value].sort(
        sortPopulations,
      );
      const hasDataShareChanges = !isEqual(
        sortedDataShares,
        sortedInitialDataShares,
      );
      const hasPopulationToPopulationChanges = !isEqual(
        currentPopulationToPopulation.value,
        initialPopulationToPopulation.value,
      );
      hasChanges.value =
        hasDataShareChanges || hasPopulationToPopulationChanges;
    },
    { deep: true, immediate: true },
  );

  // Watch to avoid a bug:
  // If user changed the current selected preset (A) with an other preset (B)
  // B became now the selected preset (but in the frontend only without saving)
  // If he deletes B (still without saving), B was still visually selected
  // Even tho correctly removes from the preset option list
  // This watch makes sure A is selected again after B deletion
  watch(
    allPresets,
    () => {
      dataShares.value.forEach((share) => {
        const defaultShare = defaultDataSharesMap.value[share.populationId];
        if (
          share.presetId &&
          !allPresets.value.some((p) => p.id === share.presetId) &&
          defaultShare
        ) {
          updateDataShare({
            populationId: share.populationId,
            visibility: share.visibility,
            presetId: defaultShare.presetId,
          });
        }
      });
    },
    { deep: true },
  );

  return {
    isLoading: computed(() => isLoading.value || !ready.value),
    dataShares: computed(() => dataShares.value.sort(sortPopulations)),
    hasChanges: computed(() => hasChanges.value),
    populationOptions,
    presetOptions,
    defaultDataSharesMap,
    singleSharePresets,
    createDefaultDataShare,
    createCustomDataShare,
    updateDataShare,
    resetDataShare,
    deleteOverride,
    bulkUpdateDataShares,
    showSuccessToast,
    currentPopulationToPopulation,
  };
}

// Helpers

function showSuccessToast() {
  const flashesStore = useFlashesStore();
  flashesStore.addSuccessFlash({ message: 'Settings saved' });
}

function addOrReplaceDataShare(
  payload: Assignments,
  populationId: number,
  newItem: DataShare,
) {
  const index = payload.data_shares.findIndex(
    (item) => item.population_id === populationId,
  );
  index !== -1
    ? (payload.data_shares[index] = newItem)
    : payload.data_shares.push(newItem);
}

function getPresetOptionsPerFeed(allPresets: DataSharePreset[]) {
  const dataSharePresetsStore = useDataSharePresetsStore();

  const options: Record<number, PresetOptionsGroup[]> = {};

  allPresets.forEach((preset: DataSharePreset) => {
    if (preset.preset_type === 'single_share_preset') return;

    // base_source_id is only filled if the preset is csv/gsheet type
    const parentId = preset.base_source_id ?? preset.feed_id;
    if (!options[parentId]) {
      options[parentId] = [
        {
          heading: null,
          items: [],
        },
      ];
    }

    const isDefault = preset.preset_type === 'crossbeam_default';
    const groupIndex = isDefault ? 0 : 1;

    if (!isDefault && options[parentId].length === 1) {
      options[parentId].splice(1, 0, {
        heading: null,
        items: [],
      });
    }

    options[parentId]?.[groupIndex]?.items.push({
      value: preset.id,
      label: dataSharePresetsStore.getPresetName(
        preset.feed_id,
        preset.base_source_id || undefined,
        preset.id,
      ),
    });
  });

  return options;
}

function sortPopulations(a: DataShareItem, b: DataShareItem) {
  const aIndex = STANDARD_POPULATION_ORDER.indexOf(a.populationName);
  const bIndex = STANDARD_POPULATION_ORDER.indexOf(b.populationName);

  if (aIndex !== -1 && bIndex !== -1) {
    return aIndex - bIndex;
  }
  if (aIndex !== -1) return -1;
  if (bIndex !== -1) return 1;

  if (!a.populationName && !b.populationName) return 0;
  if (!a.populationName) return 1;
  if (!b.populationName) return -1;
  return a.populationName.localeCompare(b.populationName);
}
